import React, { useState, useEffect, useRef } from 'react';
import styles from './TextEllipsis.module.scss';

import { Popover } from 'antd';

export default function TextEllipsis({
    children,
    maxWidth,
    ellipsis = '...',
    tooltip = true,
    minCharsPerSide = 3, // Minimum characters to show on each side
}) {
    const [isTruncated, setIsTruncated] = useState(false);
    const [displayText, setDisplayText] = useState(children);
    const containerRef = useRef(null);

    useEffect(() => {
        const updateTruncation = () => {
            const element = containerRef.current;
            if (!element) return;

            const fullText = children;
            const containerWidth = maxWidth || element.parentElement?.offsetWidth || element.offsetWidth;

            // Reset to full text to measure
            element.style.width = 'auto';
            element.textContent = fullText;

            if (element.scrollWidth > containerWidth) {
                setIsTruncated(true);
                truncateText(element, fullText, containerWidth);
            } else {
                setIsTruncated(false);
                setDisplayText(fullText);
            }
        };

        const truncateText = (element, text, maxWidth) => {
            const textLength = text.length;

            // If text is too short, just show it all
            if (textLength <= minCharsPerSide * 2 + ellipsis.length) {
                setDisplayText(text);
                return;
            }

            let leftEnd = minCharsPerSide;
            let rightStart = textLength - minCharsPerSide;

            // Binary search for optimal truncation point
            while (rightStart - leftEnd > 1) {
                const middle = Math.floor((leftEnd + rightStart) / 2);
                const truncated = `${text.substring(0, middle)}${ellipsis}${text.substring(textLength - middle)}`;

                element.textContent = truncated;

                if (element.scrollWidth > maxWidth) {
                    rightStart = middle;
                } else {
                    leftEnd = middle;
                }
            }

            // Final adjustment
            const finalText = `${text.substring(0, leftEnd)}${ellipsis}${text.substring(textLength - leftEnd)}`;
            setDisplayText(finalText);
        };

        updateTruncation();

        window.addEventListener('resize', updateTruncation);
        return () => window.removeEventListener('resize', updateTruncation);
    }, [children, maxWidth, ellipsis, minCharsPerSide]);

    return (
        <Popover content={children} trigger="hover">
            <span
                ref={containerRef}
                className={styles.container}
                style={maxWidth ? { maxWidth: `${maxWidth}px` } : {}}
                title={tooltip && isTruncated ? children : undefined}
            >
                {displayText}
            </span>
        </Popover>
    );
}
